// ProtectedRoute — wrap rutas que requieren sesión activa con claims completos.
//
// Estados que renderiza:
//   - loading: spinner mientras se hidrata la sesión
//   - signed_out: redirect a /login (preserva `from` en location.state)
//   - incomplete_claims: error UI con mensaje al admin (hook GoTrue no
//     configurado o usuario sin tenants activos)
//   - signed_in + requiredRole no matchea: 403 UI
//   - signed_in + autorizado: renderiza children
//
// Uso:
//   <Route element={<ProtectedRoute><AppShell /></ProtectedRoute>}>
//     <Route index element={<HomePage />} />
//   </Route>
//
// Para rutas con rol específico:
//   <Route element={<ProtectedRoute requiredRole="super_admin"><SuperAdminLayout /></ProtectedRoute>}>
//     ...
//   </Route>

import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import type { Role } from 'schemas';
import { useHomePath } from './useHomePath';
import { useAuth } from './useAuth';
import { useRolEfectivoVerComo } from '../ver-como';

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRole?: Role | Role[];
}

export function ProtectedRoute({ children, requiredRole }: ProtectedRouteProps) {
  const { state } = useAuth();
  const location = useLocation();
  // Modo "Ver como": el rol EFECTIVO manda para autorizar rutas (un directivo
  // suplantando a un coordinador debe pasar los gates de coordinador).
  const rolEfectivo = useRolEfectivoVerComo();

  if (state.kind === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center text-sm text-slate-500">
        <span className="size-2 animate-pulse rounded-full bg-slate-400" />
        <span className="ml-2">Cargando sesión…</span>
      </div>
    );
  }

  if (state.kind === 'signed_out') {
    // Guardamos la ruta original para redirigir después del login.
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (state.kind === 'incomplete_claims') {
    return (
      <main className="mx-auto flex min-h-screen max-w-xl flex-col items-center justify-center gap-4 p-6 text-center">
        <h1 className="text-xl font-medium text-amber-700">Sesión incompleta</h1>
        <p className="text-sm text-slate-600">
          Tu sesión está activa, pero el token no incluye los claims de tenant requeridos.
        </p>
        <p className="text-sm text-slate-600">Causas probables:</p>
        <ul className="list-disc text-left text-sm text-slate-600">
          <li>
            El hook <code>custom_access_token_hook</code> no está habilitado en GoTrue
          </li>
          <li>Su usuario no tiene tenants activos asignados</li>
        </ul>
        <p className="text-xs text-slate-500">Contacte al administrador de la plataforma.</p>
      </main>
    );
  }

  // Rol con el que se autoriza: el suplantado si el modo espejo está activo,
  // el real del JWT en caso normal.
  const rolActivo: Role = rolEfectivo ?? state.claims.rol;

  if (requiredRole) {
    const allowed = Array.isArray(requiredRole) ? requiredRole : [requiredRole];
    // super_admin siempre tiene bypass para soporte cross-tenant
    const allowedConSuperAdmin = allowed.includes('super_admin')
      ? allowed
      : ([...allowed, 'super_admin'] as Role[]);
    if (!allowedConSuperAdmin.includes(rolActivo)) {
      // En lugar de mostrar "Acceso denegado" estático, redirigimos al home
      // del rol. Caso típico: user cambió de sesión en el mismo browser y
      // quedó guardada la URL del rol anterior. La pantalla de denegado solo
      // confundía, no aportaba info útil al user.
      return (
        <RedirectAlHome pathname={location.pathname} rolActual={rolActivo} requeridos={allowed} />
      );
    }
  }

  // Personal DEDICADO a mantenimiento (rol base líder + rol de módulo): su
  // mundo es /mantenimiento. Sin este guard, un `state.from` guardado (p.ej.
  // /capturar tras el login) lo dejaba en el mundo del líder, al que su rol
  // base técnicamente autoriza. NO aplica en modo espejo (el directivo elige
  // explícitamente el mundo al que entra).
  if (rolActivo === 'lider' && requiredRole && !rolEfectivo) {
    return <GuardLiderDedicado>{children}</GuardLiderDedicado>;
  }

  return <>{children}</>;
}

/**
 * Redirige al personal dedicado a mantenimiento fuera de las rutas de líder.
 * Componente aparte porque useHomePath consulta /api/me y solo debe montarse
 * con sesión activa y rol líder ya verificados.
 */
function GuardLiderDedicado({ children }: { children: ReactNode }) {
  const { home, resolviendo } = useHomePath();
  if (resolviendo) {
    return (
      <div className="flex min-h-screen items-center justify-center text-sm text-slate-500">
        <span className="size-2 animate-pulse rounded-full bg-slate-400" />
        <span className="ml-2">Preparando su espacio de trabajo…</span>
      </div>
    );
  }
  if (home === '/mantenimiento') {
    return <Navigate to="/mantenimiento" replace />;
  }
  return <>{children}</>;
}

/**
 * Redirect al home EFECTIVO del usuario (useHomePath: rol de plataforma +
 * regla de personal dedicado a mantenimiento). Es un componente aparte porque
 * el hook consulta /api/me y solo debe montarse en la rama de rol no
 * autorizado (con sesión activa garantizada).
 */
function RedirectAlHome({
  pathname,
  rolActual,
  requeridos,
}: {
  pathname: string;
  rolActual: Role;
  requeridos: Role[];
}) {
  const { home, resolviendo } = useHomePath();

  if (resolviendo) {
    return (
      <div className="flex min-h-screen items-center justify-center text-sm text-slate-500">
        <span className="size-2 animate-pulse rounded-full bg-slate-400" />
        <span className="ml-2">Cargando sesión…</span>
      </div>
    );
  }
  // Evitamos loop infinito si por algún motivo el home tampoco matchea.
  if (pathname !== home) {
    return <Navigate to={home} replace />;
  }
  return (
    <main className="mx-auto flex min-h-screen max-w-xl flex-col items-center justify-center gap-4 p-6 text-center">
      <h1 className="text-xl font-medium text-red-700">Acceso denegado</h1>
      <p className="text-sm text-slate-600">
        Tu rol (<code>{rolActual}</code>) no está autorizado para esta vista.
      </p>
      <p className="text-xs text-slate-500">Roles requeridos: {requeridos.join(', ')}</p>
    </main>
  );
}
